// @refresh reset
import { createContext, useState, useCallback, useContext, type FC, type ReactNode } from "react";

interface NavigationBlockerContextType {
    isBlocked: boolean;
    setIsBlocked: (blocked: boolean) => void;
    pendingUrl: string | null;
    tryNavigate: (url: string) => boolean;
    confirmNavigation: () => string | null;
    cancelNavigation: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const NavigationBlockerContext = createContext<NavigationBlockerContextType>({
    isBlocked: false,
    setIsBlocked: noop,
    pendingUrl: null,
    tryNavigate: () => true,
    confirmNavigation: () => null,
    cancelNavigation: noop,
});

export const NavigationBlockerProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [isBlocked, setIsBlocked] = useState(false);
    const [pendingUrl, setPendingUrl] = useState<string | null>(null);

    const tryNavigate = useCallback(
        (url: string): boolean => {
            if (!isBlocked) return true;
            setPendingUrl(url);
            return false;
        },
        [isBlocked],
    );

    const confirmNavigation = useCallback((): string | null => {
        const url = pendingUrl;
        setPendingUrl(null);
        setIsBlocked(false);
        return url;
    }, [pendingUrl]);

    const cancelNavigation = useCallback(() => {
        setPendingUrl(null);
    }, []);

    return (
        <NavigationBlockerContext.Provider
            value={{
                isBlocked,
                setIsBlocked,
                pendingUrl,
                tryNavigate,
                confirmNavigation,
                cancelNavigation,
            }}
        >
            {children}
        </NavigationBlockerContext.Provider>
    );
};

export function useNavigationBlocker() {
    return useContext(NavigationBlockerContext);
}
